import Button from '@ui/Button';
import WishlistAction from '@c/toasts/WishlistAction';
import { addProductToFavorites } from '@util/firestore/products';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';

interface ComingSoonBannerProps {
  productId: string;
}

const ClockIcon = () => (
  <svg
    className="h-8 w-8 text-[#C75008]"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ComingSoonBanner = ({ productId }: ComingSoonBannerProps) => {
  const { userDoc } = useAuth();
  const { showToast } = useToastContext();
  const queryClient = useQueryClient();
  const [isAlreadyFavorited, setIsAlreadyFavorited] = useState(false);

  useEffect(() => {
    if (userDoc) {
      const isFavorited = userDoc.favorites?.includes(productId);
      setIsAlreadyFavorited(!!isFavorited);
    }
  }, [productId, userDoc]);

  const handleNotifyMe = () => {
    if (!userDoc) {
      window.open(
        '/login',
        'Login/Register',
        'width=600,height=700,top=0,left=0'
      );
      return;
    }

    if (isAlreadyFavorited) {
      showToast(
        <div className="flex w-full select-none items-center justify-evenly gap-[1.2rem]">
          <p className="whitespace-nowrap font-semibold text-brand-black">
            Already on your wishlist!
          </p>
        </div>
      );
      return;
    }

    addProductToFavorites(userDoc.uid, productId);
    setIsAlreadyFavorited(true);
    queryClient.invalidateQueries({ queryKey: ['user'] });
    showToast(<WishlistAction removed={false} />);
  };

  return (
    <div className="mb-6 rounded-2xl border-2 border-[#C75008] bg-[#FEF3E7] p-6 text-center">
      <div className="mb-3 flex items-center justify-center gap-3">
        <ClockIcon />
        <h2 className="text-[1.5rem] font-semibold text-[#C75008]">Coming Soon</h2>
      </div>
      <p className="mb-4 text-[#C75008]">
        This listing is pending seller verification. It will be available for
        purchase once the seller is verified.
      </p>
      <div className="flex justify-center">
        <Button
          type="primary"
          height="normal"
          text={isAlreadyFavorited ? 'On Your Wishlist' : 'Notify Me When Available'}
          onClick={handleNotifyMe}
          disabled={isAlreadyFavorited}
        />
      </div>
    </div>
  );
};

export default ComingSoonBanner;
